import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { vehicleActions } from 'state/modules/vehicles';
import { truncateKeywordSpaces } from 'utils/utils';
import { FETCH_STATE } from 'constants';

const VehiclesSelect = ({
  vehicle_group_id,
  label,
  variant,
  field: { name, value },
  customParams,
  style,
  form: { errors, touched, setFieldValue },
  disabled,
  datacy,
  id,
  inputProps,
  customNoOptionText,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const fetchingVehicles = useSelector((state) => state.vehicles.fetchingVehicles);
  const vehicles = useSelector((state) => state.vehicles.vehicles);
  const loading = fetchingVehicles === FETCH_STATE.LOADING;

  const prevvalue = usePrevious(value);

  /**
   * Fetch vehicles list
   * @param {*} params
   */
  const fetchVehicles = useCallback(
    debounce((params = {}, vehicle_group_id) => {
      const newParams = { ...params };
      if (vehicle_group_id) {
        newParams.vehicle_group_id = vehicle_group_id;
      }
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(vehicleActions.fetchVehicles(newParams));
    }, 1000),
    []
  );

  const prev = usePrevious(customParams);

  useEffect(() => {
    if (!value) return dispatch(vehicleActions.resetVehicles());
    fetchVehicles(params, vehicle_group_id);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (vehicle_group_id) {
      fetchVehicles({ ...params }, vehicle_group_id);
    }
  }, [vehicle_group_id]); // eslint-disable-line

  useEffect(() => {
    if (prev && customParams && JSON.stringify(prev) !== JSON.stringify(customParams)) {
      fetchVehicles({ ...params, ...customParams }, vehicle_group_id);
    }
  }, [customParams]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value) {
      const defaultVal = vehicles.find((el) => el?.id === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value && !defaultValue?.id) {
        dispatch(vehicleActions.fetchVehicleById(value, null, true));
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, vehicles]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue === value && vehicles.length > 0) {
      const defaultVal = vehicles.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      }
    }
  }, [vehicles]); // eslint-disable-line

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      margin="none"
      dataCy={datacy}
      fullwidth="true"
      variant={variant}
      loading={loading}
      options={vehicles}
      clearOnBlur={false}
      id={id}
      disabled={disabled}
      customNoOptionText={customNoOptionText}
      value={defaultValue}
      inputProps={{ ...inputProps }}
      filterOptions={(x) => x}
      getOptionLabel={(option) => `${option?.display_name || option?.name || ''}`}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option?.id === value?.id}
      onChange={(ev, val) => setFieldValue(name, val?.id, val || '')}
      onInputChange={(ev, val, reason) => {
        // 'reset' is MUI echoing the selected option's own label back to us, not the user
        // typing. Searching for it collapses the list to the vehicle already selected.
        if (reason === 'reset') return;
        // Drop the previous keyword rather than merging over it, so clearing the input
        // restores the unfiltered list instead of leaving a stale search applied.
        const { keyword, ...rest } = params; // eslint-disable-line no-unused-vars
        fetchVehicles(val ? { ...rest, keyword: val } : rest, vehicle_group_id);
      }}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

VehiclesSelect.propTypes = {
  id: PropTypes.string,
  datacy: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  customNoOptionText: PropTypes.string,
  vehicle_group_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  style: PropTypes.object,
  customParams: PropTypes.object,
  inputProps: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

VehiclesSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  style: {},
  inputProps: {},
  customParams: {},
  label: 'Select...',
  variant: 'standard',
  datacy: 'vehicle-select',
  noOptionsText: undefined,
};

export default VehiclesSelect;
